import { Box, Button, Skeleton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import LandingPage from './LandingPage';
import TemplateSidebar from './TemplateSidebar';
import AssistantDefination from './AssistantDefination';
// import RestaurantDetails from './RestaurantDetails';
import MenuConfiguration from './MenuConfiguration';
import Personalization from './Personalization';
import InitialAnalytics from './InitialAnalytics';
import { useSaveAssistant } from '../../hooks/useSaveAssistant';
import { useUser } from '../../hooks/useUser';
import { ValidationError } from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
  setErrors,
  setStepStatus,
  setTemplates,
} from '../../utils/redux/slices/userJourneySlice';
import LayoutWithSidebar from '../../components/common/layoutWithSidebar';
import { assistantDefinitionSchema } from '../../schemas/userJourney/assistantDefination';
import { menuConfigurationSchema } from '../../schemas/userJourney/menuConfiguration';
import { personalizeSchema } from '../../schemas/userJourney/personalize';
import { RootState } from '../../utils/redux/redux';
import { useAgents } from '../../hooks/useAgents';
import UnderDevelopment from '../common/UnderDevelopement';
import { ArrowLeft } from '@phosphor-icons/react';
import { useNavigate, useLocation } from 'react-router';
import { usePosIntegrationData } from '../../hooks/usePosIntegrationData';
import { useTemplates } from '../../hooks/useTemplates';
// import { routes } from '../../constants/routes';

const UserJourneyFlow = () => {
  const location = useLocation();
  const [selectedTemplate, setSelectedTemplate] = useState<any>(
    location.state?.selectedTemplate || null
  );
  const [selectedSection, setSelectedSection] = useState<string | undefined>(
    undefined
  );
  const [selectedSubSection, setSelectedSubSection] =
    useState('assistant-name');
  const navigate = useNavigate();

  const { assistantDefinition, menuConfig, personalization } = useSelector(
    (state: RootState) => state.userJourney
  );
  // const { templates } = useSelector((state: RootState) => state.userJourney);

  const { saveAssistant, isPending } = useSaveAssistant();
  const { user } = useUser();
  const { agents } = useAgents();
  const dispatch = useDispatch();
  const { data } = useTemplates();
  const { data: posIntegrationData } = usePosIntegrationData();

  const isJourneyComplete =
    user?.journey?.businessDetails &&
    user?.journey?.brandDetails &&
    user?.journey?.storeDetails;

  const shouldShowMenuConfig = !posIntegrationData?.data?.data?.length;

  useEffect(() => {
    if (posIntegrationData && !selectedSection) {
      if (shouldShowMenuConfig) {
        setSelectedSection('menu-configuration');
      } else {
        setSelectedSection('assistant-definition');
      }
    }
  }, [posIntegrationData, shouldShowMenuConfig, selectedSection]);

  const getSections = () => {
    const baseSection = [
      'assistant-definition',
      'personalization',
      'analytics',
    ];
    return shouldShowMenuConfig
      ? ['menu-configuration', ...baseSection]
      : baseSection;
  };

  useEffect(() => {
    if (data?.data?.templates) {
      const templates = data.data.templates.map((template: any) => ({
        templateId: template._id,
        templateName: template.name,
      }));
      dispatch(setTemplates(templates));
    }
  }, [data?.data?.templates, dispatch]);

  useEffect(() => {
    // Set template from location state if available
    if (location.state?.selectedTemplate) {
      setSelectedTemplate(location.state.selectedTemplate);
    }
  }, [location.state]);

  // useEffect(() => {
  //   if (agents?.agents?.length > 0) {
  //     const currentAgent = agents.agents[0];
  //     const template = templates.find(
  //       (template: any) => template.templateId === currentAgent.templateId
  //     );
  //     setSelectedTemplate(template);
  //   }
  // }, [agents, templates]);

  const handleSectionChange = (section: string, subSection?: string) => {
    setSelectedSection(section);
    if (subSection) {
      setSelectedSubSection(subSection);
    } else {
      // Set default subSection for each section
      const defaultSubSections = {
        'assistant-definition': 'assistant-name',
        // 'menu-configuration': 'pos-integration',
        personalization: 'greeting',
        analytics: 'call-stage',
      } as const;
      setSelectedSubSection(
        defaultSubSections[section as keyof typeof defaultSubSections]
      );
    }
  };

  const handleGetStarted = () => {
    setSelectedSection('assistant-definition');
    setSelectedSubSection('assistant-name');
  };

  const handleGetBack = () => {
    setSelectedSection('menu-configuration');
  };

  const handleNext = async () => {
    try {
      // Validate current section data based on selectedSection
      if (selectedSection === 'menu-configuration') {
        await menuConfigurationSchema.validate(menuConfig.data, {
          abortEarly: false,
        });
      } else if (selectedSection === 'assistant-definition') {
        await assistantDefinitionSchema.validate(assistantDefinition.data, {
          abortEarly: false,
        });
      } else if (selectedSection === 'personalization') {
        await personalizeSchema.validate(personalization.data, {
          abortEarly: false,
        });
      }

      // Save assistant data
      await saveAssistant({
        userId: user?._id,
        assistantId:
          agents?.agents?.length > 1
            ? agents.agents[agents.agents.length - 1]._id
            : agents?.agents?.[0]?._id,
        businessId: user?.business?._id,
        status: 'draft',
        section: selectedSection as
          | 'assistant-definition'
          | 'menu-configuration'
          | 'personalization'
          | 'analytics',
      });

      // Don't automatically navigate if we're in menu-configuration
      // Let MenuConfiguration handle its own navigation
      if (selectedSection !== 'menu-configuration') {
        const sections = getSections();
        const currentIndex = sections.indexOf(selectedSection!);
        if (currentIndex < sections.length - 1) {
          handleSectionChange(sections[currentIndex + 1]);
        }
      }
    } catch (err) {
      if (err instanceof ValidationError) {
        const newErrors: Record<string, string> = {};
        err.inner.forEach(error => {
          newErrors[error.path!] = error.message;
        });
        dispatch(setErrors(newErrors));
        dispatch(
          setStepStatus({
            step: selectedSection as
              | 'assistantDefinition'
              | 'menuConfig'
              | 'personalization',
            status: { isComplete: false, hasError: true },
          })
        );
      } else {
        console.error(err);
      }
    }
  };

  const handlePrev = () => {
    const sections = getSections();
    const currentIndex = sections.indexOf(selectedSection ?? ''); // Default to ''

    if (currentIndex > 0) {
      handleSectionChange(sections[currentIndex - 1]);
    } else if (
      !shouldShowMenuConfig &&
      selectedSection === 'assistant-definition'
    ) {
      navigate('/add-new-assistant');
    }
  };

  const handleTemplateSelect = (template: any) => {
    // Replace spaces with hyphens instead of using URL encoding
    const formattedName = template.name.replace(/\s+/g, '-').toLowerCase();
    navigate(`/add-new-assistant/${formattedName}`, {
      state: { selectedTemplate: template },
    });
    setSelectedTemplate(template);
  };

  if (isJourneyComplete && !selectedTemplate) {
    return (
      <LayoutWithSidebar>
        <LandingPage onTemplateSelect={handleTemplateSelect} />;
      </LayoutWithSidebar>
    );
  }

  const formatSectionTitle = (section: string) => {
    if (!section) return '';

    return section
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join('-');
  };

  if (selectedTemplate?.name !== 'Food Ordering' && selectedTemplate === null) {
    return (
      <LayoutWithSidebar>
        <Box
          sx={{
            px: { xs: 2, sm: 4, md: 10 },
            backgroundColor: 'white',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          {/* Header Section Skeleton */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: { xs: 3, sm: 4, md: 4 },
              width: '100%',
            }}
          >
            <Skeleton variant="text" width="60%" height={40} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="80%" height={24} />
          </Box>

          {/* Template Cards Skeleton */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 2,
              width: '100%',
              maxWidth: '1400px',
              margin: '0 auto',
            }}
          >
            {Array.from(new Array(3)).map((_, index) => (
              <Box
                key={index}
                sx={{
                  border: '1.5px solid lightgray',
                  borderRadius: '12px',
                  p: { xs: 2, md: 3 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Skeleton
                  variant="text"
                  width="60%"
                  height={28}
                  sx={{ mb: 1 }}
                />
                <Skeleton
                  variant="text"
                  width="100%"
                  height={20}
                  sx={{ mb: 0.5 }}
                />
                <Skeleton
                  variant="text"
                  width="90%"
                  height={20}
                  sx={{ mb: 2 }}
                />

                {/* Feature list skeletons */}
                {Array.from(new Array(3)).map((_, featureIndex) => (
                  <Box
                    key={featureIndex}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Skeleton variant="circular" width={16} height={16} />
                    <Skeleton
                      variant="text"
                      width="90%"
                      height={20}
                      sx={{ ml: 1 }}
                    />
                  </Box>
                ))}

                <Box sx={{ mt: 'auto', pt: 2 }}>
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={40}
                    sx={{ borderRadius: '6px' }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </LayoutWithSidebar>
    );
  }

  if (selectedTemplate?.name === 'General Queries') {
    return (
      <LayoutWithSidebar>
        <Button
          variant="text"
          startIcon={<ArrowLeft size={16} />}
          sx={{
            width: 'fit-content',
            textTransform: 'none',
            color: '',
            fontWeight: 500,
            fontSize: '16px',
            boxShadow: 'none',
            pt: 2,
            px: 2,
            '&:hover': {
              backgroundColor: 'transparent',
              boxShadow: 'none',
            },
          }}
          onClick={() => navigate('/add-new-assistant')}
        >
          Previous
        </Button>
        <UnderDevelopment />
      </LayoutWithSidebar>
    );
  }

  return (
    <LayoutWithSidebar>
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'white',
          py: 0.2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderBottom: '1px solid #E0E0E0',
            p: 2,
            backgroundColor: 'white',
          }}
        >
          <Typography
            variant="h6"
            color="custom.textBlue"
            sx={{ textAlign: 'center' }}
          >
            <span
              style={{
                fontSize: '16px',
                fontWeight: 400,
                color: 'lightGray.text',
              }}
            >
              {selectedTemplate?.name}
            </span>
            <span
              style={{
                fontSize: '16px',
                margin: '0 8px',
                color: 'custom.textBlue',
              }}
            >
              /
            </span>
            <span
              style={{
                fontSize: '16px',
                fontWeight: 500,
                color: 'custom.textBlue',
              }}
            >
              {selectedSection && formatSectionTitle(selectedSection)}
            </span>
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            // width: 'calc(100vw - 75px)',
            height: 'calc(100vh - 64px)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <TemplateSidebar
            templateName={selectedTemplate?.name}
            selectedSection={selectedSection || ''}
            selectedSubSection={selectedSubSection}
            onSectionChange={handleSectionChange}
            shouldShowMenuConfigFlow={shouldShowMenuConfig}
          />
          {selectedSection && (
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {selectedSection === 'assistant-definition' && (
                <AssistantDefination
                  selectedSubSection={selectedSubSection}
                  onNext={handleNext}
                  onPrev={handlePrev}
                  isPending={isPending}
                  shouldShowMenuConfigFlow={shouldShowMenuConfig}
                />
              )}

              {shouldShowMenuConfig &&
                selectedSection === 'menu-configuration' && (
                  <MenuConfiguration
                    selectedSubSection={selectedSubSection}
                    onNext={handleNext}
                    isPending={isPending}
                    handleGetStarted={handleGetStarted}
                    handleGetBack={handleGetBack}
                  />
                )}
              {selectedSection === 'personalization' && (
                <Personalization
                  selectedSubSection={selectedSubSection}
                  onNext={handleNext}
                  onPrev={handlePrev}
                  isPending={isPending}
                />
              )}
              {selectedSection === 'analytics' && (
                <InitialAnalytics
                  onPrev={handlePrev}
                  shouldShowMenuConfigFlow={shouldShowMenuConfig}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
    </LayoutWithSidebar>
  );
};

export default UserJourneyFlow;
